import { useEffect } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Group, Stack, Text } from "@mantine/core";
import { useChangePinMutation } from "../../../../hooks/queries/useAuthQueries";
import authService from "../../../../services/api/authService";
import { toastService } from "../../../../providers";
import { extractAxiosErrorMessage } from "../../../../services/utils/errorUtils";
import { parsePinCode } from "../../../../services/utils/pinAuthUtils";
import {
  PIN_CHANGE_ERROR_MESSAGES,
  AUTH_STEP_UP_CODES,
} from "../../../auth/config/authActionCodes";
import { applyPinLockedBlock } from "../../../shared/otpErrorUtils";
import { usePinLockedStore } from "../../../../stores/useOtpBlockStore";
import { pinValidator } from "../../../../lib/validations/authValidations";
import { CustomPinInput } from "../../../ui/CustomPin/CustomPin";
import { CustomText } from "../../../ui/CustomText/CustomText";
import CustomButton from "../../../ui/CustomButton/CustomButton";
import CustomModal from "../../../ui/CustomModal/CustomModal";
import PinSetupFields from "../../../auth/PinSetupFields/PinSetupFields";

const DEFAULT_VALUES = { currentPin: "", newPin: "", confirmPin: "" };

export default function ChangePinModal({ opened, onClose }) {
  const navigate = useNavigate();
  const { mutate: changePin, isPending } = useChangePinMutation();

  const {
    control,
    handleSubmit,
    setError,
    reset,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    mode: "onTouched",
  });

  const currentPin = useWatch({ control, name: "currentPin" });
  const newPin = useWatch({ control, name: "newPin" });
  const confirmPin = useWatch({ control, name: "confirmPin" });

  const canSubmit =
    currentPin?.length === 6 &&
    newPin?.length === 6 &&
    confirmPin?.length === 6 &&
    Object.keys(errors).length === 0;

  useEffect(() => {
    if (newPin) trigger("newPin");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPin]);

  const handleClose = () => {
    reset(DEFAULT_VALUES);
    onClose();
  };

  const handlePinLockedResponse = (data) => {
    applyPinLockedBlock(data, usePinLockedStore, "PIN_LOCKED");
    handleClose();
    toastService.error("Your PIN is currently locked. Please try again later.");
  };

  const onSubmit = (values) => {
    changePin(
      {
        currentPin: values.currentPin,
        newPin: values.newPin,
        confirmPin: values.confirmPin,
      },
      {
        onSuccess: (data) => {
          const code = parsePinCode(null, data);

          if (code === AUTH_STEP_UP_CODES.PIN_LOCKED) {
            handlePinLockedResponse(data);
            return;
          }

          authService.logout();
          navigate("/auth/login", { state: { bannerReason: "pin_changed" } });
        },
        onError: (error) => {
          const code = error?.response?.data?.error;

          if (code === AUTH_STEP_UP_CODES.PIN_LOCKED) {
            handlePinLockedResponse(error?.response?.data);
            return;
          }

          const mapped = PIN_CHANGE_ERROR_MESSAGES[code];

          if (mapped) {
            setError(mapped.field, { message: mapped.message });
            return;
          }

          toastService.error(
            extractAxiosErrorMessage(
              error,
              "Could not change your PIN. Please try again.",
            ),
          );
        },
      },
    );
  };

  return (
    <CustomModal
      opened={opened}
      onClose={handleClose}
      centered
      size="md"
      radius="md"
      title={
        <Text fw={600} size="lg" style={{ textAlign: "center" }}>
          Change PIN
        </Text>
      }
      overlayProps={{ backgroundOpacity: 0.5, blur: 2 }}
      closeOnClickOutside={!isPending}
      closeOnEscape={!isPending}
      withCloseButton={!isPending}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="md">
          <Stack gap="md" align="center">
            <CustomText size="sm" c="dimmed" align="center">
              Current PIN
            </CustomText>
            <Controller
              name="currentPin"
              control={control}
              rules={{ validate: (v) => pinValidator(v) ?? true }}
              render={({ field, fieldState }) => (
                <>
                  <CustomPinInput
                    length={6}
                    mask
                    disabled={isPending}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                  {fieldState.error && (
                    <CustomText c="error" size="sm" align="center">
                      {fieldState.error.message}
                    </CustomText>
                  )}
                </>
              )}
            />

            <PinSetupFields
              control={control}
              disabled={isPending}
              trigger={trigger}
              validateNewPin={(v) =>
                v === currentPin
                  ? "New PIN must be different from your current PIN."
                  : null
              }
            />
          </Stack>

          <Group mt="md" justify="flex-end">
            <CustomButton
              type="button"
              variant="default"
              onClick={handleClose}
              disabled={isPending}
            >
              Cancel
            </CustomButton>
            <CustomButton
              type="submit"
              loading={isPending}
              disabled={isPending || !canSubmit}
            >
              Change PIN
            </CustomButton>
          </Group>
        </Stack>
      </form>
    </CustomModal>
  );
}
